import { useState, useEffect } from 'react'
import {
  X,
  Lock,
  Users,
  UserCheck,
  Link2,
  Check,
  Loader2,
  Trash2,
} from 'lucide-react'
import {
  listAppShares,
  shareApp,
  removeAppShare,
  updateSharePermission,
  updateApp,
  type App,
  type AppShare,
} from '../lib/api'
import { withMountBase } from '../lib/mountBase'
import { StyledSelect } from './ui/StyledSelect'

interface ShareModalProps {
  app: App
  onClose: () => void
  onShared?: () => void
}

function getAccessLevelOptions(isPublished: boolean) {
  return [
    { value: 'personal', label: 'Private', description: 'Only you can access', icon: Lock },
    { value: 'specific', label: 'Specific people', description: 'Only people you add', icon: UserCheck },
    { value: 'link', label: 'Anyone with the link', description: 'Any platform user with the link', icon: Link2 },
    {
      value: 'platform',
      label: 'Platform',
      description: isPublished
        ? 'Visible to all platform users in Browse Tab'
        : 'Publish the app first to make it visible in Browse Tab',
      icon: Users,
      disabled: !isPublished,
    },
  ]
}

export default function ShareModal({ app, onClose, onShared }: ShareModalProps) {
  const [shares, setShares] = useState<AppShare[]>([])
  const [sharesLoading, setSharesLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [permission, setPermission] = useState('view')
  const [sharing, setSharing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const [currentAccessLevel, setCurrentAccessLevel] = useState(app.access_level)
  const [accessLevelLoading, setAccessLevelLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const canManageShares = app.my_permission === 'owner' || app.my_permission === 'edit' || app.my_permission === 'admin'

  useEffect(() => {
    loadShares()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.name])

  async function loadShares() {
    try {
      const data = await listAppShares(app.name)
      setShares(data)
    } catch {
      // silently fail for non-owners
    } finally {
      setSharesLoading(false)
    }
  }

  async function handleAccessLevelChange(newLevel: string) {
    setAccessLevelLoading(true)
    setError(null)
    const prevLevel = currentAccessLevel
    setCurrentAccessLevel(newLevel)
    try {
      await updateApp(app.name, { access_level: newLevel } as Partial<App>)
      onShared?.()
    } catch (err) {
      setCurrentAccessLevel(prevLevel)
      setError(err instanceof Error ? err.message : 'Failed to update access level')
    } finally {
      setAccessLevelLoading(false)
    }
  }

  async function handleShare(e: React.FormEvent) {
    e.preventDefault()
    setSharing(true)
    setError(null)
    try {
      await shareApp(app.name, email, permission)
      setEmail('')
      await loadShares()
      onShared?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share app')
    } finally {
      setSharing(false)
    }
  }

  async function handleUpdatePermission(shareEmail: string, newPerm: string) {
    setError(null)
    try {
      await updateSharePermission(app.name, shareEmail, newPerm)
      await loadShares()
      onShared?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update permission')
    }
  }

  async function handleRemove(shareEmail: string) {
    setRemoving(shareEmail)
    try {
      await removeAppShare(app.name, shareEmail)
      await loadShares()
      onShared?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove share')
    } finally {
      setRemoving(null)
    }
  }

  function handleCopyLink() {
    const appPath = withMountBase(`/apps/${app.name}`, window.location.pathname)
    const url = `${window.location.origin}${appPath}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-semibold">Share &ldquo;{app.display_name || app.name}&rdquo;</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Control who can access your app</p>

        {/* Access Level Selector */}
        {canManageShares && (
          <div className="mb-5">
            <p className="text-sm font-medium mb-2">General access</p>
            <StyledSelect
              value={currentAccessLevel}
              onChange={handleAccessLevelChange}
              disabled={accessLevelLoading}
              options={getAccessLevelOptions(app.production_status === 'running' || (app.production_status == null && app.access_level === 'platform'))}
              className="w-full"
            />
            {currentAccessLevel === 'link' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Visible to anyone with the link (<em>not visible in Browse Tab</em>).
              </p>
            )}
            {currentAccessLevel === 'platform' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Visible to all platform users in Browse Tab
              </p>
            )}
          </div>
        )}

        {/* Copy link */}
        <div className="mt-3 mb-5">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors flex-1 text-sm"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
              {copied ? 'Link copied!' : 'Copy link'}
            </button>
          </div>
          {(currentAccessLevel === 'personal' || currentAccessLevel === 'specific') && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Only people with access can use this link
            </p>
          )}
        </div>

        {/* Share with specific people */}
        {canManageShares && (
          <div className="border-t border-border pt-4 mb-4">
            <p className="text-sm font-medium mb-3">Share with specific people</p>
            <form onSubmit={handleShare} className="flex gap-2 mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              <StyledSelect
                value={permission}
                onChange={setPermission}
                options={[
                  { value: 'view', label: 'Viewer' },
                  { value: 'edit', label: 'Editor' },
                ]}
              />
              <button
                type="submit"
                disabled={sharing || !email}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </button>
            </form>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
            {error}
          </div>
        )}

        {/* Shares List with per-user role dropdowns */}
        <div className="space-y-2 mb-5">
          {sharesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : shares.length === 0 ? (
            <p className="text-center text-muted-foreground py-3 text-sm">
              No individual shares yet
            </p>
          ) : (
            shares.map((share) => (
              <div
                key={share.id}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{share.email}</div>
                  {!share.user_registered && (
                    <div className="text-xs text-yellow-500">Pending invite</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canManageShares ? (
                    <>
                      <StyledSelect
                        value={share.permission}
                        onChange={(newPerm) => handleUpdatePermission(share.email, newPerm)}
                        options={[
                          { value: 'view', label: 'Viewer' },
                          { value: 'edit', label: 'Editor' },
                        ]}
                      />
                      <button
                        onClick={() => handleRemove(share.email)}
                        disabled={removing === share.email}
                        className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                      >
                        {removing === share.email ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground capitalize">{share.permission}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm"
        >
          Done
        </button>
      </div>
    </div>
  )
}
